<form [formGroup]="form.formGroup">
    <p-fieldset legend="Rheology">
        <div class="flex flex-col space-y-2">
            <div class="flex flex-col">
                <label for="density">Density</label>
                <app-validated-number-input
                    [id]="'density'"
                    [control]="control('density')"
                    [value]="control('density').value"
                    [validation]="{ min: 0, max: 1000000 }"
                    [unit]="densityUnits"
                ></app-validated-number-input>
            </div>

            <div class="flex flex-col">
                <label for="nPrime">n'</label>
                <app-validated-number-input
                    [id]="'nPrime'"
                    [control]="control('nPrime')"
                    [value]="control('nPrime').value"
                    [validation]="{ min: 0, max: 100 }"
                ></app-validated-number-input>
            </div>

            <div class="flex flex-col">
                <label for="kPrime">K'</label>
                <app-validated-number-input
                    [id]="'kPrime'"
                    [control]="control('kPrime')"
                    [value]="control('kPrime').value"
                    [validation]="{ min: 0, max: 100 }"
                    [unit]="kPrimeUnit"
                    [unitPrecisionOverrides]="{ 'lb*s^n\'/ft²': 5, 'Pa*s^n': 5 }"
                ></app-validated-number-input>
            </div>

            <div class="flex flex-col">
                <label for="rheologyTemperature">Rheology Temperature</label>
                <app-validated-number-input
                [id]="'rheologyTemperature'"
                [control]="control('rheologyTemperature')"
                [value]="control('rheologyTemperature').value"
                [validation]="temperatureValidation"
                [unit]="tempUnit">
                </app-validated-number-input>
            </div>
        </div>
    </p-fieldset>

    <br>

    <p-fieldset>
        <ng-template pTemplate="header">
            <div class="flex align-items-center h-5">
                <p-toggleswitch formControlName="modelFrictionReduction" id="modelFrictionReduction" binary="true" (onChange)="modelFrictionReductionChange()">
                </p-toggleswitch>
                <label class="ml-2"> Model Friction Reduction</label>
            </div>
        </ng-template>
        
        <div class="flex align-items-center pb-4">
            <p-toggleswitch [disabled]="!disableFrictionReduction" (onChange)="tablePlotToggle()"></p-toggleswitch>
            <label class="pl-1">Show Plot</label>

            @if (showPlot) {
                <div class="flex align-items-center h-5 pl-4">
                    <p-toggleswitch binary="true" (onChange)="onPlotFrictionFactorChange()"></p-toggleswitch>
                    <label class="pl-1">Plot Friction Factor</label>
                </div>

                <div class="flex align-items-center h-5 pl-4">
                    <p-toggleswitch binary="true" (onChange)="onLogChange()"></p-toggleswitch>
                    <label class="pl-1">Log</label>
                </div>
            } @else {
                <div class="flex align-items-center h-5 pl-4">
                    <p-toggleswitch [disabled]="!disableFrictionReduction" binary="true" (onChange)="onFrictionFactorTableChange()"></p-toggleswitch>
                    <label class="pl-1">Show Friction Factors</label>
                </div>
            }
        </div>
        
        @if (showPlot) {
            <div class="w-[100%] pb-4">
                <app-xy-line-plot [plotData]="plot.data" [xAxisTitle]="xAxisTitle" [yAxisTitle]="yAxisTitle" [plotName]="plotName"
                    [downloadPlotName]="plotName" [reverseAutoRange]="true" [plotTitle]="'Fluids'"
                    [xyLinePlotStore]="fluidsStore">
                </app-xy-line-plot>
            </div>
        } @else {
            <div class="pb-5" [ngClass]="{ 'opacity-50 pointer-events-none': (!disableFrictionReduction || isInUse) }">
                @if (!showFrictionFactorTable) {
                    <app-units-grid-input
                        [tableTitle]="'Polymer Friction Pressure Loss'"
                        [rows]="frictionalPressureLossRows"
                        [columns]="frictionalPressureLossColumns"
                        [userUnits]="userUnits"
                        [autoHeight]="true"
                        [autoHeightOffset]="115"
                        [allowDeleteRows]="true"
                        [minRows]="1"
                        [createRow]="createFrictionalPressureLossRow"
                        [commitMode]="'debounced'"
                        [commitDebounceMs]="300"
                        (cellEdited)="onFrictionalPressureLossCellEdited()"
                        (rowDeleted)="onFrictionalPressureLossRowDeleted($event)"
                        (rowsReset)="onFrictionalPressureLossRowsReset()"
                        (pasteApplied)="onFrictionalPressureLossGridCommit()"
                        (commitRequested)="onFrictionalPressureLossGridCommit()"
                    />
                } @else {
                    <app-ng-table-results
                        tableName="Friction Factors"
                        [tableData]="resultsGrid"
                        [columnDefinitions]="cols"
                        [autoHeight]="true"
                    ></app-ng-table-results>
                }
            </div>
        }

        <p-fieldset legend="{{ plotFrictionFactor || showFrictionFactorTable ? 'Friction Factor Interpolation' : 'Pressure Loss Interpolation' }}">
            <form [formGroup]="interpForm">
                <div class="pb-4">
                    <label>Pipe Inner Diameter</label>
                    <p-input-group>
                        <p-inputNumber 
                            [minFractionDigits]="1" 
                            formControlName="innerDiameterInput"
                            (onBlur)="interpolatePressureLoss()">
                        </p-inputNumber>
                        <p-inputgroup-addon>{{ diameterUnits }}</p-inputgroup-addon>
                    </p-input-group>

                    <label>Flow Rate</label>
                    <p-input-group>
                        <p-inputNumber 
                            [minFractionDigits]="1" 
                            formControlName="flowRateInput"
                            (onBlur)="interpolatePressureLoss()">
                        </p-inputNumber>
                        <p-inputgroup-addon>{{ flowRateUnits }}</p-inputgroup-addon>
                    </p-input-group>

                    @if (!plotFrictionFactor && !showFrictionFactorTable) {
                        <label>Interpolated Pressure Loss</label>
                    }
                    @if (plotFrictionFactor || showFrictionFactorTable) {
                        <label>Interpolated Friction Factor</label>
                    }
                    <p-input-group>
                        <p-inputNumber
                            [minFractionDigits]="5"
                            [ngModel]="interpolatedPoint"
                            [disabled]="true"
                            [ngModelOptions]="{ standalone: true }"
                        ></p-inputNumber>
                        @if (!plotFrictionFactor && !showFrictionFactorTable) {
                            <p-inputgroup-addon>{{ pressureLossUnits }}</p-inputgroup-addon>
                        }
                    </p-input-group>
                </div>
            </form>
        </p-fieldset>
    </p-fieldset>
</form>